import { useMemo, useState, type ReactNode } from "react";

import { Plural, Trans, useLingui } from "@lingui/react/macro";
import { ApiError } from "@pontimed/api-client";
import { cn } from "@pontimed/ui";
import { Link } from "@tanstack/react-router";
import { CalendarDays, CirclePause, CirclePlay, Info } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ResponsiveSheet } from "@/components/ui/responsive-sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { apiErrorCode } from "@/lib/api-error-message";
import { isVaultUnlockCancelled, useProtectedAction } from "@/lib/hooks/use-protected-action";
import {
  ceilToLocalDay,
  isValidPauseDate,
  pauseDateBounds,
  previewPauseEnd,
  usePauseServices,
  useResumeServices,
  useServicePause,
  useServicePauseLiveSync,
  type ServicePauseDuration,
  type ServicePauseEntity,
  type ServicePauseState,
  type ServicePauseSubscription,
} from "@/lib/hooks/use-service-pause";
import { showToast } from "@/lib/ui/toast";

/** Display name per subscription id — the billing view's `planDisplayName`. */
type PlanNames = Readonly<Record<string, string>>;

/**
 * "Pause services" card on a workspace's billing page (`_docs/service-pause.md`).
 * Not paused: what a pause does + the sheet that starts one. Paused: until
 * when, the paid days that carry over, and "Restart now".
 */
export function ServicePauseCard({
  planNames,
  ...entity
}: ServicePauseEntity & {
  /** The plans' display names, from the billing data the page already holds. */
  planNames?: PlanNames;
}) {
  useServicePauseLiveSync();
  const query = useServicePause(entity);
  const [sheetOpen, setSheetOpen] = useState(false);
  // "Now" for the pause sheet, fixed when it opens (see PauseServicesSheet).
  const [sheetOpenedAt, setSheetOpenedAt] = useState(() => new Date());
  const [resumeOpen, setResumeOpen] = useState(false);

  if (query.isPending) {
    return (
      <div className="bg-card space-y-3 rounded-xl border p-4 sm:p-6" aria-busy="true">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-4 w-full max-w-md" />
        <Skeleton className="h-11 w-44 rounded-lg" />
      </div>
    );
  }

  if (query.isError) {
    // No billing rights on this workspace (staff seat) → the card is not theirs.
    if (
      query.error instanceof ApiError &&
      (query.error.status === 403 || query.error.status === 404)
    ) {
      return null;
    }
    return (
      <div className="bg-card flex flex-wrap items-center justify-between gap-3 rounded-xl border p-4 sm:p-6">
        <p className="text-muted-foreground text-sm">
          <Trans>Could not load the service pause.</Trans>
        </p>
        <Button variant="outline" size="touch" onClick={() => void query.refetch()}>
          <Trans>Retry</Trans>
        </Button>
      </div>
    );
  }

  const state = query.data;

  return (
    <>
      {state.paused ? (
        <PausedCard state={state} planNames={planNames} onResume={() => setResumeOpen(true)} />
      ) : (
        <div className="bg-card rounded-xl border p-4 sm:p-6">
          <div className="flex items-center gap-2">
            <CirclePause className="text-primary size-5" aria-hidden />
            <h2 className="text-foreground text-lg font-semibold">
              <Trans>Pause services</Trans>
            </h2>
          </div>
          <p className="text-muted-foreground mt-2 max-w-prose text-sm">
            <Trans>
              Taking a break? Close new bookings and stop billing for a while. Your profile stays
              public and the paid days you have left are kept.
            </Trans>
          </p>
          <Button
            variant="outline"
            size="touch"
            className="mt-4 rounded-lg"
            onClick={() => {
              setSheetOpenedAt(new Date());
              setSheetOpen(true);
            }}
          >
            <CirclePause aria-hidden />
            <Trans>Stop services</Trans>
          </Button>
        </div>
      )}

      <PauseServicesSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        entity={entity}
        current={state}
        planNames={planNames}
        now={sheetOpenedAt}
      />
      <ResumeServicesSheet
        open={resumeOpen}
        onClose={() => setResumeOpen(false)}
        entity={entity}
        state={state}
      />
    </>
  );
}

/* ───────────── Shared bits ───────────── */

export function useFormatPauseDate() {
  const { i18n } = useLingui();
  return (iso: string) =>
    new Date(iso).toLocaleDateString(i18n.locale, {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
}

/**
 * The plan's sold name. `planTier` is a code ("provider_pro"), never shown
 * as-is; its last word is only the fallback for a plan the billing data lacks.
 */
function planLabel(sub: ServicePauseSubscription, planNames: PlanNames | undefined): string {
  const name = planNames?.[sub.id];
  if (name) return name;
  const word = sub.planTier.split("_").pop() ?? "";
  return word ? word.charAt(0).toUpperCase() + word.slice(1) : "";
}

/** Translated message for a refused pause / restart call. */
function usePauseErrorMessage() {
  const { t } = useLingui();
  return (err: unknown, fallback: string): string => {
    switch (apiErrorCode(err)) {
      case "PAST_DUE":
        return t`A payment is overdue. Pay it first, then you can pause your services.`;
      case "ALREADY_PAUSED":
        return t`Services are already paused.`;
      case "NOT_PAUSED":
        return t`Services are not paused anymore.`;
      case "INVALID_UNTIL":
        // The API reads the date in the clinic's / doctor's country time zone.
        return t`That date cannot be used. Pick a date at least two days from now and within 12 months.`;
      case "CARRY_TOO_LONG":
        return t`This pause plus the paid days you have left would run too far ahead for billing. Choose a shorter pause.`;
      case "PAUSE_PARTIAL":
        return t`Billing could not be fully updated, so your services are still paused. Please try again in a few minutes.`;
      default:
        return fallback;
    }
  };
}

/** Link to the workspace calendar; businesses have none. */
function CalendarLink({ entity, children }: { entity: ServicePauseEntity; children: ReactNode }) {
  const className =
    "text-primary inline-flex min-h-11 items-center gap-1.5 text-sm font-medium hover:underline";
  if (entity.entityType === "provider") {
    return (
      <Link to="/account/provider/calendar" className={className}>
        <CalendarDays className="size-4" aria-hidden />
        {children}
      </Link>
    );
  }
  if (entity.entityType === "facility") {
    return (
      <Link
        to="/account/facility/$facilityId/calendar"
        params={{ facilityId: entity.entityId }}
        className={className}
      >
        <CalendarDays className="size-4" aria-hidden />
        {children}
      </Link>
    );
  }
  return null;
}

function AppointmentsLine({ count, entity }: { count: number; entity: ServicePauseEntity }) {
  if (entity.entityType === "business") return null;
  return (
    <div className="space-y-0.5">
      <p className="text-foreground text-sm">
        {count > 0 ? (
          <Plural
            value={count}
            one="# appointment is already booked in this period. It stays as it is."
            other="# appointments are already booked in this period. They stay as they are."
          />
        ) : (
          <Trans>No appointments are booked in this period.</Trans>
        )}
      </p>
      {count > 0 && (
        <CalendarLink entity={entity}>
          <Trans>Open calendar</Trans>
        </CalendarLink>
      )}
    </div>
  );
}

function BillingLines({
  subscriptions,
  planNames,
}: {
  subscriptions: ServicePauseSubscription[];
  planNames: PlanNames | undefined;
}) {
  if (subscriptions.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">
        <Trans>You have no paid plan here, so there is nothing to bill.</Trans>
      </p>
    );
  }
  return (
    <ul className="space-y-2">
      {subscriptions.map((sub) => {
        const plan = planLabel(sub, planNames);
        const days = sub.carriedDays ?? 0;
        return (
          <li key={sub.id} className="text-sm">
            <span className="text-foreground font-medium">{plan}</span>
            <span className="text-muted-foreground">
              {" · "}
              {days > 0 ? (
                <Plural
                  value={days}
                  one="billing stops; # paid day is added when you restart."
                  other="billing stops; # paid days are added when you restart."
                />
              ) : (
                <Trans>billing stops while services are paused.</Trans>
              )}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/* ───────────── Paused state ───────────── */

function PausedCard({
  state,
  planNames,
  onResume,
}: {
  state: ServicePauseState;
  planNames: PlanNames | undefined;
  onResume: () => void;
}) {
  const formatDate = useFormatPauseDate();
  const until = state.pausedUntil ? formatDate(state.pausedUntil) : null;
  return (
    <div className="border-warning-border bg-card rounded-xl border p-4 sm:p-6">
      <div className="flex flex-wrap items-center gap-2">
        <CirclePause className="text-warning size-5" aria-hidden />
        <h2 className="text-foreground text-lg font-semibold">
          <Trans>Pause services</Trans>
        </h2>
        <Badge variant="warning">
          <Trans>Paused</Trans>
        </Badge>
      </div>
      <p className="text-foreground mt-3 text-sm font-medium">
        {until ? (
          <Trans>Services are stopped until {until}.</Trans>
        ) : (
          <Trans>Services are stopped until you restart them.</Trans>
        )}
      </p>
      <p className="text-muted-foreground mt-1 text-sm">
        <Trans>New bookings are closed and your plan is not billed.</Trans>
      </p>

      <div className="mt-4 space-y-3 border-t pt-4">
        <AppointmentsLine
          count={state.upcomingAppointments}
          entity={{ entityType: state.entityType, entityId: state.entityId }}
        />
        <BillingLines subscriptions={state.subscriptions} planNames={planNames} />
      </div>

      <Button size="touch" className="mt-4 rounded-lg" onClick={onResume}>
        <CirclePlay aria-hidden />
        <Trans>Restart now</Trans>
      </Button>
    </div>
  );
}

/* ───────────── Pause sheet ───────────── */

function PauseServicesSheet({
  open,
  onClose,
  entity,
  current,
  planNames,
  now,
}: {
  open: boolean;
  onClose: () => void;
  entity: ServicePauseEntity;
  current: ServicePauseState;
  planNames: PlanNames | undefined;
  /**
   * When the sheet was opened. Fixed for its lifetime: a `new Date()` per
   * render moved the preview end — and the query key it feeds — on every
   * render, a request loop.
   */
  now: Date;
}) {
  const { t } = useLingui();
  const formatDate = useFormatPauseDate();
  const errorMessage = usePauseErrorMessage();
  const pause = usePauseServices();
  // POST is behind the billing vault PIN: a vault_locked refusal asks for it
  // and retries once.
  const { runProtected, PinGateElement } = useProtectedAction("billing");
  const [duration, setDuration] = useState<ServicePauseDuration>("1m");
  const [dateValue, setDateValue] = useState("");
  const bounds = useMemo(() => pauseDateBounds(now), [now]);

  const dateInvalid = duration === "date" && !isValidPauseDate(dateValue, now);
  const previewEnd = useMemo(
    () => previewPauseEnd(duration, dateValue, now),
    [duration, dateValue, now],
  );
  // Appointments in the chosen window — the API counts them (`until`). The
  // window is rounded up to whole days, so reopening the sheet hits the cache.
  const preview = useServicePause(entity, {
    until: previewEnd ? ceilToLocalDay(previewEnd) : null,
    enabled: open && !dateInvalid,
    keepPrevious: true,
  });
  const previewState = preview.data ?? current;

  const options: { id: ServicePauseDuration; label: string }[] = [
    { id: "1w", label: t`1 week` },
    { id: "1m", label: t`1 month` },
    { id: "3m", label: t`3 months` },
    { id: "date", label: t`Until a date` },
    { id: "indefinite", label: t`Until I restart them` },
  ];

  const previewDate = previewEnd ? formatDate(previewEnd) : null;
  const profileLine = previewDate
    ? t`Your profile stays public and shows “Not taking appointments until ${previewDate}”.`
    : t`Your profile stays public and shows “Temporarily unavailable”.`;

  const submit = async () => {
    if (dateInvalid || pause.isPending) return;
    const toast = showToast(t`Stopping services…`, "loading");
    try {
      await runProtected(() =>
        pause.mutateAsync({
          ...entity,
          duration,
          ...(duration === "date" ? { until: dateValue } : {}),
        }),
      );
      toast.update(t`Services stopped`, "success");
      onClose();
    } catch (err) {
      // PIN gate dismissed: nothing was sent.
      if (isVaultUnlockCancelled(err)) toast.dismiss();
      else toast.update(errorMessage(err, t`Could not stop services. Please try again.`), "error");
    }
  };

  return (
    <>
      <ResponsiveSheet
        open={open}
        onClose={onClose}
        title={<Trans>Stop services</Trans>}
        description={<Trans>Choose how long. You can restart them at any time.</Trans>}
        width="md"
        footer={
          <div className="flex gap-2">
            <Button variant="outline" className="min-h-[44px] flex-1 rounded-lg" onClick={onClose}>
              <Trans>Cancel</Trans>
            </Button>
            <Button
              className="min-h-[44px] flex-1 rounded-lg"
              disabled={dateInvalid || pause.isPending}
              onClick={() => void submit()}
            >
              <CirclePause aria-hidden />
              <Trans>Stop services</Trans>
            </Button>
          </div>
        }
      >
        <div className="space-y-5 p-5">
          {/* Duration */}
          <fieldset className="space-y-2">
            <legend className="text-foreground mb-2 text-sm font-semibold">
              <Trans>For how long?</Trans>
            </legend>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {options.map((opt) => {
                const selected = duration === opt.id;
                return (
                  <label
                    key={opt.id}
                    className={cn(
                      "flex min-h-11 cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 text-sm transition-colors",
                      selected
                        ? "border-primary bg-primary-soft text-foreground font-medium"
                        : "border-border hover:bg-muted/50",
                      opt.id === "indefinite" && "sm:col-span-2",
                    )}
                  >
                    <input
                      type="radio"
                      name="service-pause-duration"
                      value={opt.id}
                      checked={selected}
                      onChange={() => setDuration(opt.id)}
                      className="accent-primary size-4 shrink-0"
                    />
                    {opt.label}
                  </label>
                );
              })}
            </div>
            {duration === "date" && (
              <div className="pt-1">
                <label
                  htmlFor="service-pause-until"
                  className="text-foreground mb-1 block text-sm font-medium"
                >
                  <Trans>Restart on</Trans>
                </label>
                <Input
                  id="service-pause-until"
                  type="date"
                  min={bounds.min}
                  max={bounds.max}
                  value={dateValue}
                  onChange={(e) => setDateValue(e.target.value)}
                  aria-invalid={dateValue !== "" && dateInvalid}
                  className="h-11 sm:max-w-xs"
                />
                {dateValue !== "" && dateInvalid && (
                  <p className="text-destructive mt-1 text-xs">
                    <Trans>Pick a date at least two days from now and within 12 months.</Trans>
                  </p>
                )}
              </div>
            )}
          </fieldset>

          {/* What happens */}
          <section className="space-y-2">
            <h3 className="text-foreground text-sm font-semibold">
              <Trans>What happens</Trans>
            </h3>
            <ul className="text-muted-foreground list-disc space-y-1 pl-5 text-sm">
              <li>
                <Trans>New bookings are closed.</Trans>
              </li>
              <li>{profileLine}</li>
              <li>
                <Trans>Appointments already booked stay as they are. Nothing is cancelled.</Trans>
              </li>
            </ul>
          </section>

          {/* Booked appointments in the window */}
          {entity.entityType !== "business" && !dateInvalid && (
            <section className="bg-muted/40 rounded-lg p-3">
              {preview.isPending ? (
                <Skeleton className="h-5 w-full max-w-sm" />
              ) : (
                <div
                  className={cn("transition-opacity", preview.isPlaceholderData && "opacity-60")}
                  aria-busy={preview.isPlaceholderData}
                >
                  <AppointmentsLine count={previewState.upcomingAppointments} entity={entity} />
                </div>
              )}
            </section>
          )}

          {/* Billing */}
          <section className="space-y-2">
            <h3 className="text-foreground text-sm font-semibold">
              <Trans>Billing</Trans>
            </h3>
            <BillingLines subscriptions={previewState.subscriptions} planNames={planNames} />
            {previewState.subscriptions.length > 0 && (
              <p className="text-muted-foreground flex items-start gap-1.5 text-xs">
                <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden />
                <Trans>Nothing is refunded and nothing is lost: your plan resumes as it was.</Trans>
              </p>
            )}
          </section>
        </div>
      </ResponsiveSheet>
      {PinGateElement}
    </>
  );
}

/* ───────────── Restart confirmation ───────────── */

export function ResumeServicesSheet({
  open,
  onClose,
  entity,
  state,
}: {
  open: boolean;
  onClose: () => void;
  entity: ServicePauseEntity;
  state: ServicePauseState;
}) {
  const { t } = useLingui();
  const errorMessage = usePauseErrorMessage();
  const resume = useResumeServices();
  const { runProtected, PinGateElement } = useProtectedAction("billing");
  const totalDays = state.subscriptions.reduce((sum, s) => sum + (s.carriedDays ?? 0), 0);

  const submit = async () => {
    if (resume.isPending) return;
    const toast = showToast(t`Restarting services…`, "loading");
    try {
      await runProtected(() => resume.mutateAsync(entity));
      toast.update(t`Services restarted`, "success");
      onClose();
    } catch (err) {
      if (isVaultUnlockCancelled(err)) {
        toast.dismiss();
        return;
      }
      toast.update(errorMessage(err, t`Could not restart services. Please try again.`), "error");
      if (apiErrorCode(err) === "NOT_PAUSED") onClose();
    }
  };

  return (
    <>
      <ResponsiveSheet
        open={open}
        onClose={onClose}
        title={<Trans>Restart services now?</Trans>}
        width="sm"
        footer={
          <div className="flex gap-2">
            <Button variant="outline" className="min-h-[44px] flex-1 rounded-lg" onClick={onClose}>
              <Trans>Cancel</Trans>
            </Button>
            <Button
              className="min-h-[44px] flex-1 rounded-lg"
              disabled={resume.isPending}
              onClick={() => void submit()}
            >
              <CirclePlay aria-hidden />
              <Trans>Restart now</Trans>
            </Button>
          </div>
        }
      >
        <div className="space-y-2 p-5 text-sm">
          <p className="text-foreground">
            <Trans>New bookings open again right away.</Trans>
          </p>
          {state.subscriptions.length > 0 && (
            <p className="text-muted-foreground">
              {totalDays > 0 ? (
                <Plural
                  value={totalDays}
                  one="Billing resumes, and # paid day you had left is added to your plan."
                  other="Billing resumes, and the # paid days you had left are added to your plan."
                />
              ) : (
                <Trans>Billing resumes as it was.</Trans>
              )}
            </p>
          )}
        </div>
      </ResponsiveSheet>
      {PinGateElement}
    </>
  );
}
